/* eslint-disable no-underscore-dangle */
import axios, { AxiosInstance, CreateAxiosDefaults } from 'axios'
import { useAuthStore } from '@/feature/auth/store/authStore'
import { posthogClient } from './posthog'

type RefreshTokenResponse = {
  access_token: string
  refresh_token: string
  expires_in: number
  refresh_expires_in: number
}

export const getRefreshToken = () => useAuthStore.getState().session?.refresh_token ?? window.localStorage.getItem(`refresh_token`)

export const removeAuthTokens = () => {
  window.localStorage.removeItem(`access_token`)
  window.localStorage.removeItem(`refresh_token`)
  useAuthStore.setState({
    session: null,
    user: null
  })
}

export const setRefreshToken = (token: string) => {
  useAuthStore.setState({
    session: {
      ...useAuthStore.getState().session!,
      refresh_token: token
    }
  })
}

const handleRefreshToken = async (instance: AxiosInstance, refresh_t: string) => {
  const response = await instance.post<RefreshTokenResponse>(`/auth/refresh-token`, {
    refresh_token: refresh_t
  })

  useAuthStore.setState({
    session: {
      ...useAuthStore.getState().session!,
      access_token: response.data.access_token,
      refresh_token: response.data.refresh_token,
      expires_in: response.data.expires_in,
      refresh_expires_in: response.data.refresh_expires_in
    }
  })
  return response.data
}

const setupInterceptors = (instance: AxiosInstance) => {
  instance.interceptors.request.use(
    config => {
      const accessToken = useAuthStore.getState().session?.access_token

      if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`
      }

      return config
    },
    error => Promise.reject(error)
  )

  instance.interceptors.response.use(
    response => response,

    async error => {
      const originalRequest = error.config
      let refreshToken: string | null = null
      try {
        refreshToken = getRefreshToken() as string
      } catch (_e) {
        return Promise.reject(_e)
      }
      if (!refreshToken) {
        return Promise.reject(error)
      }

      if (error.response?.status === 401 && originalRequest.url.includes(`/auth/refresh-token`)) {
        removeAuthTokens()
        window.location.href = `/auth/logout`
        return Promise.reject(error)
      }

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true
        try {
          const { access_token, refresh_token } = await handleRefreshToken(instance, refreshToken)
          setAuthToken(access_token)
          setRefreshToken(refresh_token)
          return instance(originalRequest)
        } catch (refreshError) {
          removeAuthTokens()
          window.location.href = `/auth/logout`
          return Promise.reject(refreshError)
        }
      }
      return Promise.reject(error)
    }
  )

  instance.interceptors.response.use(
    response => response,

    error => {
      const originalRequest = error.config
      if (error.response) {
        console.error('API error response', {
          url: originalRequest?.url,
          method: originalRequest?.method,
          status: error.response.status,
          data: error.response.data
        })
      }
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        if (posthogClient?.__loaded) {
          if (posthogClient.config.capture_exceptions) {
            if (originalRequest && !originalRequest._retry) {
              posthogClient.captureException(error, {
                extra: {
                  url: originalRequest.url,
                  method: originalRequest.method,
                  status: error.response?.status
                }
              })
            }
          } else if (import.meta.env.DEV) console.warn(`Capture exceptions is disabled`)
        } else {
          console.warn(`PostHog is not loaded`)
        }

        originalRequest._retry = true
      }
      return Promise.reject(error)
    }
  )

  return instance
}

export const createAxiosInstance = (isNextJS = false) => {
  const baseURL = isNextJS ? import.meta.env.VITE_NEXTJS_APP_API_URL : import.meta.env.VITE_API_URL

  const baseConfig: CreateAxiosDefaults = {
    baseURL: `${baseURL}`
  }

  const instance = axios.create(baseConfig)
  return setupInterceptors(instance)
}

export const axiosInstance = createAxiosInstance(false)
export const axiosNextJSInstance = createAxiosInstance(true)

export const setAuthToken = (token: string) => {
  axiosInstance.defaults.headers.common.Authorization = `Bearer ${token}`
  axiosNextJSInstance.defaults.headers.common.Authorization = `Bearer ${token}`
}
